import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, MapPin, Lock, Wrench, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const SchlosswechselPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Wie lange dauert ein Schlosswechsel?',
      answer: 'Ein einfacher Zylindertausch dauert in der Regel nur 15-30 Minuten. Wir bringen gängige Zylinder in verschiedenen Sicherheitsstufen mit, sodass wir direkt vor Ort arbeiten können.'
    },
    {
      question: 'Kann ich mehrere Türen auf einen Schlüssel umstellen lassen?',
      answer: 'Ja, das ist möglich! Wir können eine Schließanlage einrichten, bei der ein Schlüssel mehrere Türen öffnet. Dies ist besonders praktisch bei Mehrfamilienhäusern oder wenn Sie Haustür und Wohnungstür mit einem Schlüssel öffnen möchten.'
    },
    {
      question: 'Welche Kosten kommen ungefähr auf mich zu?',
      answer: 'Die Kosten hängen von der Qualität und Sicherheitsstufe des Zylinders ab. Einfache Zylinder gibt es ab etwa 30€, hochsichere Sicherheitszylinder können 80-150€ kosten. Wir beraten Sie transparent vor Ort.'
    },
    {
      question: 'Muss ich bei Mietwohnungen den Vermieter fragen?',
      answer: 'Bei baulichen Veränderungen sollten Sie idealerweise den Vermieter informieren. Ein Zylindertausch ist aber reversibel – Sie können beim Auszug den alten Zylinder wieder einbauen lassen.'
    },
    {
      question: 'Bekomme ich mehrere Schlüssel?',
      answer: 'Ja, zu jedem neuen Zylinder erhalten Sie in der Regel 3-5 Schlüssel. Bei Bedarf können weitere Schlüssel nachbestellt werden.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Schlosswechsel & Zylindertausch in Dortmund – Mehr Sicherheit für Ihr Zuhause</title>
        <meta name="description" content="Schlosswechsel in Dortmund gewünscht? Wir tauschen Türschlösser & Schließzylinder fachgerecht – z.B. nach Einbruch, Schlüsselverlust oder Umzug. Jetzt beraten lassen!" />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/schlosswechsel/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Schlosswechsel' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Schlosswechsel & Zylindertausch in Dortmund
        </h1>

        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Neue Wohnung bezogen? Schlüssel verloren? Nach einem Einbruch?</strong> Es gibt viele gute Gründe 
            für einen Schlosswechsel. Als erfahrener Schlüsseldienst in Dortmund tauschen wir Türschlösser und 
            Schließzylinder fachgerecht und beraten Sie zu passenden Sicherheitslösungen.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Ein neuer Schließzylinder gibt Ihnen die Sicherheit, dass nur Sie und berechtigte Personen Zugang 
            zu Ihrer Wohnung oder Ihrem Haus haben. Besonders nach einem Schlüsselverlust oder bei Einzug in 
            eine neue Wohnung ist ein Zylindertausch aus Sicherheitsgründen dringend zu empfehlen.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG bietet Ihnen professionellen Service, transparente Beratung und 
            hochwertige Schließzylinder in verschiedenen Sicherheitsstufen. Wir sind schnell vor Ort und 
            erledigen den Einbau fachgerecht.
          </p>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Schlosswechsel gewünscht?</h3>
                <p className="text-gray-700">Wir beraten Sie zu passenden Zylindern – jetzt anrufen!</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Fachgerechter Einbau</strong> – schnell und sauber ausgeführt</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Qualitativ gute Zylinder</strong> in verschiedenen Sicherheitsstufen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Beratung vor Ort</strong> zu passenden Schließsystemen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Transparente Preise</strong> – Material und Arbeitszeit klar besprochen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Schneller Service</strong> – meist am selben Tag möglich</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Schließanlagen möglich</strong> – ein Schlüssel für mehrere Türen</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Wann ist ein Schlosswechsel sinnvoll?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-lg">Nach Einzug oder Umzug</h3>
              </div>
              <p className="text-gray-700">
                Sie wissen nicht, wer noch Schlüssel zur Wohnung hat? Vormieter, Handwerker oder Vermieter 
                könnten noch Zugang haben. Ein neuer Zylinder gibt Ihnen die Sicherheit, dass nur Sie den 
                Schlüssel besitzen.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-lg">Bei Schlüsselverlust</h3>
              </div>
              <p className="text-gray-700">
                Haben Sie Ihren Schlüssel verloren oder wurde er gestohlen? Aus Sicherheitsgründen sollten Sie 
                den Zylinder umgehend tauschen, um unbefugten Zugang zu verhindern.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <Wrench className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-lg">Nach Trennung</h3>
              </div>
              <p className="text-gray-700">
                Bei einer Trennung möchten viele Menschen sicherstellen, dass der Ex-Partner keinen Zugang 
                mehr zur Wohnung hat. Ein Zylindertausch schafft hier schnell Klarheit.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-orange-600" />
                <h3 className="font-bold text-lg">Nach Einbruch</h3>
              </div>
              <p className="text-gray-700">
                Nach einem Einbruch sollte das Schloss auf jeden Fall getauscht werden – oft auch ein guter 
                Zeitpunkt, auf einen hochsicheren Zylinder aufzurüsten.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Ablauf & Kosten</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3">So läuft der Schlosswechsel ab</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">1.</span>
                    <span><strong>Vor-Ort-Besuch:</strong> Wir kommen zu Ihnen und begutachten die Tür und das aktuelle Schloss</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">2.</span>
                    <span><strong>Beratung:</strong> Wir empfehlen Ihnen einen passenden Zylinder basierend auf Ihren Sicherheitsbedürfnissen</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">3.</span>
                    <span><strong>Einbau:</strong> Der alte Zylinder wird ausgebaut und der neue fachgerecht eingesetzt (15-30 Min)</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-bold text-orange-600">4.</span>
                    <span><strong>Funktionsprüfung:</strong> Wir testen den neuen Zylinder und übergeben Ihnen die Schlüssel</span>
                  </li>
                </ol>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3">Kosten transparent besprochen</h3>
                <p className="text-gray-700 mb-4">
                  Die Gesamtkosten setzen sich zusammen aus:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Arbeitszeit:</strong> Je nach Aufwand, meist pauschal</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Material:</strong> Zylinder je nach Sicherheitsstufe (ca. 30-150€)</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span><strong>Anfahrt:</strong> In Dortmund meist inklusive</span>
                  </li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Wir besprechen alle Kosten transparent mit Ihnen, bevor wir mit der Arbeit beginnen.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Kombination mit Einbruchschutz</h2>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
            <p className="text-gray-700 mb-4">
              <strong>Nutzen Sie die Gelegenheit!</strong> Wenn Sie ohnehin einen Zylinder tauschen lassen, 
              ist dies der ideale Zeitpunkt, um auch über zusätzliche Sicherheitsmaßnahmen nachzudenken.
            </p>
            <p className="text-gray-700 mb-4">
              Ein hochwertiger Sicherheitszylinder mit Aufbohr-, Zieh- und Pickschutz macht es Einbrechern 
              deutlich schwerer. In Kombination mit weiteren Sicherungen (z.B. Panzerriegel) erhöhen Sie 
              Ihren Schutz erheblich.
            </p>
            <Link 
              to="/leistungen/einbruchschutz"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all"
            >
              Mehr zum Einbruchschutz
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einsatzgebiet in Dortmund</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Wir sind in allen Stadtteilen von Dortmund für Sie da.</strong> Termine meist noch am 
                selben oder nächsten Tag möglich.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Schlosswechsel gewünscht? Jetzt beraten lassen!</h2>
          <p className="text-lg mb-6 text-gray-300">
            Fachgerechter Einbau • Transparente Preise • Hochwertige Zylinder<br />
            Termine meist noch am selben Tag möglich
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/leistungen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Alle Leistungen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/tueroeffnung-verschlossen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Türöffnung verschlossen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/einbruchschutz" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Einbruchschutz <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchlosswechselPage;
